import {
  Box,
  MenuList as MuiMenuList,
  MenuListProps as MuiMenuListProps,
  Typography,
} from "@mui/material";
import { forwardRef } from "react";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";

/**
 * 메뉴 리스트 컨테이너 props — `MenuItem` 자식 + 선택사항으로 헤더(전체선택) / 푸터(액션 버튼).
 */
export interface MenuListProps extends Omit<MuiMenuListProps, "children"> {
  children?: React.ReactNode;
  /** 푸터 버튼 노출 */
  hasButton?: boolean;
  buttonText?: string;
  buttonDisabled?: boolean;
  onButtonClick?: () => void;
  maxHeight?: number;
  /** 헤더에 "전체 선택" 체크박스 노출 */
  hasSelectAll?: boolean;
  selectAll?: boolean;
  /** 부분 선택 시 indeterminate UI 표시 */
  selectAllIndeterminate?: boolean;
  onSelectAllChange?: (checked: boolean) => void;
  /** 컨테이너에 그림자 적용 */
  shadow?: boolean;
}

const MenuListStyles = {
  container: {
    default: {
      backgroundColor: "#FFFFFF",
      borderRadius: "8px",
      minWidth: "200px",
    },
    withShadow: {
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
    },
  },
  header: {
    hasSelectAll: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      padding: "0 1px",
      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    },
  },
  content: {
    default: {
      padding: "6px",
    },
  },
  footer: {
    button: {
      padding: "8px",
      borderTop: "1px solid rgba(0, 0, 0, 0.08)",
      display: "flex",
      justifyContent: "stretch",
      alignItems: "center",
    },
  },
  selectAllLabel: {
    fontSize: "14px",
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.87)",
    lineHeight: "18px",
    letterSpacing: "0.25px",
  },
} as const;

export const MenuList = forwardRef<HTMLUListElement, MenuListProps>(
  (
    {
      children,
      hasButton = false,
      buttonText = "완료",
      buttonDisabled = false,
      onButtonClick,
      maxHeight,
      hasSelectAll = false,
      selectAll = false,
      selectAllIndeterminate = false,
      onSelectAllChange,
      shadow = false,
      sx,
      ...muiProps
    },
    ref
  ) => {
    const containerSx = {
      ...MenuListStyles.container.default,
      ...(shadow ? MenuListStyles.container.withShadow : {}),
    };

    const contentSx = {
      ...MenuListStyles.content.default,
      ...(typeof maxHeight === "number" && maxHeight > 0
        ? {
            maxHeight: `${maxHeight}px`,
            overflowY: "auto" as const,
          }
        : {}),
    };

    return (
      <Box sx={{ ...containerSx, ...sx }}>
        {hasSelectAll && (
          <Box sx={MenuListStyles.header.hasSelectAll}>
            <Checkbox
              size="medium"
              checked={selectAll}
              indeterminate={selectAllIndeterminate}
              onChange={(event) => onSelectAllChange?.(event.target.checked)}
            />
            <Typography sx={MenuListStyles.selectAllLabel}>
              전체 선택
            </Typography>
          </Box>
        )}
        <MuiMenuList ref={ref} sx={contentSx} {...muiProps}>
          {children}
        </MuiMenuList>
        {hasButton && (
          <Box sx={MenuListStyles.footer.button}>
            <Button
              size="medium"
              emphasis="contained"
              intent="primary"
              disabled={buttonDisabled}
              onClick={onButtonClick}
              sx={{
                width: "100%",
                height: "30px",
                padding: "10px",
                borderRadius: "4px",
              }}
            >
              {buttonText}
            </Button>
          </Box>
        )}
      </Box>
    );
  }
);

MenuList.displayName = "MenuList";
